import Immutable, { List, OrderedSet } from 'immutable';
import { IntegrationsSelectedItem } from 'records/IntegrationsSelectedItem';
import { ExportSortDirection } from 'records/Export';
import { Markup } from 'records/Markup';
import { NewRFI, RawNewRFI } from 'records/NewRFI';
import { NewRFIAttachment } from 'records/NewRFIAttachment';
import { NewRFIForwardActivity } from 'records/NewRFIForwardActivity';
import { ProjectId } from 'records/Project';
import { ProjectDriveSelectedItem } from 'records/ProjectDriveSelectedItem';
import { RawRFI, RFI, RFIId, RFIList, StudioData } from 'records/RFI';
import { RawRFIAnswer, RFIAnswer } from 'records/RFIAnswer';
import {
  RawRFIAttachment,
  RFIAttachment,
  RFIAttachmentTypes,
} from 'records/RFIAttachment';
import { RFIFile } from 'records/RFIFile';
import { RawRFIResponse, RFIResponse } from 'records/RFIResponse';
import { StudioFolderImportRequest } from 'records/StudioFolderImportRequest';
import { StudioImportRequest } from 'records/StudioImportRequest';
import { StudioProjectFileData } from 'records/StudioProjectFile';
import { UploadData } from 'records/UploadData';
import { beginUpload } from 'services/fileService';
import GQLRequest from 'utils/GQLRequest';
import { DmsProviderKind } from 'records/ProcessingStatus';

export interface RFIExportItem {
  url: string;
  extension: string;
}

export interface NewRFIAttachmentUpload {
  projectid: ProjectId;
  parentid: string;
  fileid: string;
  name: string;
  type: keyof typeof RFIAttachmentTypes;
}

interface ImportRfiAttachmentsFromDmsResponse {
  success: boolean;
  attachments: RawRFIAttachment[];
}

type ImportRfiAttachmentsFromDmsIntegrationRequest = {
  projectid: string;
  rfiid: string;
  imports: {
    driveid: string;
    fileid: string;
    connectionid: string;
    dmstype: DmsProviderKind;
  }[];
  isanswer: boolean;
};

interface ImportRfiAttachmentsFromDmsIntegrationResponse {
  success: boolean;
  attachments: RawRFIAttachment[];
}

export interface RFIDraftUpdate {
  rfiid: RFIId;
  newdata: RawNewRFI;
  attachmentsToDelete: string[]; // file ids?
}

const RFISchema = `
id
projectid
title
question
duedate
craeteddate
draftcreationdate
assignees {
  id
  email
}
expectedAttachmentCount
attachments {
  rfiid
  fileid
  name
  type
}
answer {
  id
  projectid
  answer
  pageids
  attachments {
    rfiid
    fileid
    name
    type
  }
  reportinguseremail
  respondingdate
  reviewingemail
  reviewingname
  reviewingcompany
}
pageids
number
ispublished
submittingname
submittingemail
submittingcompany
requestinguseremail
distributionlist
impactcategory {
  kind
  display
}
forwardinglist
forwardactivity {
  id
  message
  attachments {
    rfiid
    fileid
    name
    type
  }
  pageids
  email
  timestamp
}
postedmarkups {
  markupid
  fileid
  projectid
  jsonBlob
}
postedmarkupids
discussionattachmentids
previousversionid
nextversionid
versionnumber
`;

async function addProjectDriveFilesToRFI(input: {
  projectid: string;
  rfiid: string;
  imports: {
    driveid: string;
    fileid: string;
  }[];
  isanswer: boolean;
}): Promise<ImportRfiAttachmentsFromDmsResponse> {
  const resp = await new GQLRequest()
    .query(
      `
        mutation ImportRfiAttachmentsFromDms($input: ImportRfiAttachmentsFromDmsRequest) {
          importRfiAttachmentsFromDms(input: $input) {
            success
            attachments {
              rfiid
              fileid
              name
              type              
            }
          }
        }
      `
    )
    .variables({ input })
    .json<{
      importRfiAttachmentsFromDms: ImportRfiAttachmentsFromDmsResponse;
    }>();

  return resp.importRfiAttachmentsFromDms;
}

async function addIntegrationFilesToRFI(
  input: ImportRfiAttachmentsFromDmsIntegrationRequest
): Promise<ImportRfiAttachmentsFromDmsIntegrationResponse> {
  const resp = await new GQLRequest()
    .query(
      `
        mutation ImportRfiAttachmentsFromDmsIntegration($input: ImportRfiAttachmentsFromDmsIntegrationRequest) {
          importRfiAttachmentsFromDmsIntegration(input: $input) {
            success
            attachments {
              rfiid
              fileid
              name
              type              
            }
          }
        }
      `
    )
    .variables({ input })
    .json<{
      importRfiAttachmentsFromDmsIntegration: ImportRfiAttachmentsFromDmsIntegrationResponse;
    }>();

  return resp.importRfiAttachmentsFromDmsIntegration;
}

async function addProjectDriveItemsToRFI(input: {
  projectid: string;
  rfiid: string;
  projectDriveItems: List<ProjectDriveSelectedItem>;
  isAnswer: boolean;
}): Promise<ImportRfiAttachmentsFromDmsResponse> {
  const { projectid, rfiid, projectDriveItems, isAnswer } = input;

  if (projectDriveItems) {
    const projectDriveFiles = projectDriveItems.filter(
      (item) => item.itemType === 'file'
    );

    if (projectDriveFiles.count() > 0) {
      return addProjectDriveFilesToRFI({
        projectid,
        rfiid,
        imports: projectDriveFiles
          .map((file) => ({
            driveid: file.driveId,
            fileid: file.id,
          }))
          .toArray(),
        isanswer: isAnswer,
      });
    }
  }

  return Promise.resolve({ success: true, attachments: [] });
}

async function addIntegrationItemsToRFI(input: {
  projectid: string;
  rfiid: string;
  integrationItems?: List<IntegrationsSelectedItem>;
  isAnswer: boolean;
}): Promise<ImportRfiAttachmentsFromDmsResponse> {
  const { projectid, rfiid, integrationItems, isAnswer } = input;

  if (integrationItems) {
    const integrationFiles = integrationItems.filter(
      (item) =>
        item.itemType === 'file' && item.providerType && item.connectionId
    );

    if (integrationFiles.count() > 0) {
      return addIntegrationFilesToRFI({
        projectid,
        rfiid,
        imports: integrationFiles
          .map((file) => ({
            driveid: file.driveId,
            fileid: file.id,
            dmstype:
              DmsProviderKind[
                file.providerType.toUpperCase() as keyof typeof DmsProviderKind
              ],
            connectionid: file.connectionId,
          }))
          .toArray(),
        isanswer: isAnswer,
      });
    }
  }

  return Promise.resolve({ success: true, attachments: [] });
}

export function converRFIFileToUploadDataTuple(
  rfiFile: RFIFile
): [RFIFile, UploadData] {
  return [
    rfiFile,
    new UploadData({
      projectid: rfiFile.projectid,
      filename: rfiFile.file.name,
      category: rfiFile.category,
      extension: `.${rfiFile.file.name.split('.').pop()}`,
      filesize: rfiFile.file.size,
      file: rfiFile.file,
    }),
  ];
}

export async function getNextRFIs(cursor: string): Promise<RFIResponse> {
  const { viewNextRfis } = await new GQLRequest()
    .query(
      `
      query NextRFIs($cursor: String!) {
        viewNextRfis(cursor: $cursor) {
          rfis {
            ${RFISchema}
          }
          cursor
        }
      }
      `
    )
    .variables({ cursor })
    .json<{ viewNextRfis: RawRFIResponse }>();

  return RFIResponse.deserialize(viewNextRfis);
}

export async function getRFIs(
  projectid: string,
  pageSize = 1000
): Promise<RFIList> {
  const { viewRfis } = await new GQLRequest()
    .query(
      `
      query RFIs($projectid: String!, $pageSize: Int!) {
        viewRfis(projectid: $projectid, pageSize: $pageSize) {
          rfis {
            ${RFISchema}
          }
          cursor
        }
      }
      `
    )
    .variables({ projectid, pageSize })
    .json<{ viewRfis: RawRFIResponse }>();

  let rfisList = Immutable.List<RFI>(viewRfis.rfis.map(RFI.deserialize));
  let { cursor } = viewRfis;

  while (cursor) {
    const { rfis: nextRFIs, cursor: nextCursor } = await getNextRFIs(cursor);

    rfisList = rfisList.concat(nextRFIs);
    cursor = nextCursor;
  }

  return rfisList;
}

export async function getRFIDrafts(
  projectid: string,
  pageSize = 1000
): Promise<RFIList> {
  const { getRfiDrafts } = await new GQLRequest()
    .query(
      `
      query RFIDrafts($projectid: String!) {
        getRfiDrafts(projectid: $projectid) {
          ${RFISchema}
        }
      }
      `
    )
    .variables({ projectid, pageSize })
    .json<{ getRfiDrafts: RawRFI[] }>();

  const rfiDraftsList = Immutable.List<RFI>(getRfiDrafts.map(RFI.deserialize));

  return rfiDraftsList;
}

export async function completeRFIAttachmentUpload(
  input: NewRFIAttachmentUpload
): Promise<RawRFIAttachment> {
  const resp = await new GQLRequest()
    .query(
      `
      mutation CompleteRfiAttachmentUpload($input: NewRFIAttachment) {
        completeRfiAttachmentUpload(input: $input) {
          rfiid
          fileid
          name
          type
        }
      }
    `
    )
    .variables({ input })
    .json<{ completeRfiAttachmentUpload: RawRFIAttachment }>();

  return resp.completeRfiAttachmentUpload;
}

export async function completeRfiAnswerAttachmentUpload(
  input: NewRFIAttachmentUpload
): Promise<RawRFIAttachment> {
  const resp = await new GQLRequest()
    .query(
      `
        mutation CompleteRfiAnswerAttachmentUpload($input: NewRFIAttachment) {
          completeRfiAnswerAttachmentUpload(input: $input) {
            rfiid
            fileid
            name
            type
          }
        }
      `
    )
    .variables({ input })
    .json<{ completeRfiAnswerAttachmentUpload: RawRFIAttachment }>();

  return resp.completeRfiAnswerAttachmentUpload;
}

export async function addRfiAttachmentsFromStudio(
  projectid: string,
  rfiid: string,
  studioFiles: StudioImportRequest[],
  studioFolders: StudioFolderImportRequest[]
): Promise<RawRFIAttachment[]> {
  const parentid = rfiid;

  const resp = await new GQLRequest()
    .query(
      `
        mutation addRfiAttachmentsFromStudio($projectid: String!, $parentid: String!, $studioFiles: [StudioImportRequest!], $studioFolders: [StudioFolderImportRequest!]) {
          addRfiAttachmentsFromStudio(projectid: $projectid, parentid: $parentid, studioFiles: $studioFiles, studioFolders: $studioFolders) {
            rfiid
            fileid
            name
            type
          }
        }
      `
    )
    .variables({
      projectid,
      parentid,
      studioFiles,
      studioFolders,
    })
    .json<{ addRfiAttachmentsFromStudio: RawRFIAttachment[] }>();

  return resp.addRfiAttachmentsFromStudio;
}

export async function addRfiAnswerAttachmentsFromStudio(
  projectid: string,
  rfiid: string,
  studioFiles: StudioImportRequest[],
  studioFolders: StudioFolderImportRequest[]
): Promise<RawRFIAttachment[]> {
  const parentid = rfiid;

  const resp = await new GQLRequest()
    .query(
      `
        mutation addRfiAnswerAttachmentsFromStudio($projectid: String!, $parentid: String!, $studioFiles: [StudioImportRequest!], $studioFolders: [StudioFolderImportRequest!]) {
          addRfiAnswerAttachmentsFromStudio(projectid: $projectid, parentid: $parentid, studioFiles: $studioFiles, studioFolders: $studioFolders) {
            rfiid
            fileid
            name
            type
          }
        }
      `
    )
    .variables({
      projectid,
      parentid,
      studioFiles,
      studioFolders,
    })
    .json<{ addRfiAnswerAttachmentsFromStudio: RawRFIAttachment[] }>();

  return resp.addRfiAnswerAttachmentsFromStudio;
}

const attachFilesToRFI = async ({
  projectid,
  rfiid,
  files,
  isAnswer,
  projectDriveItems,
  integrationItems,
  studioItems,
  studioData,
}: {
  projectid: string;
  rfiid: string;
  files: Immutable.List<RFIFile>;
  isAnswer: boolean;
  projectDriveItems: List<ProjectDriveSelectedItem>;
  studioItems: List<StudioProjectFileData>;
  studioData: StudioData;
  integrationItems: List<IntegrationsSelectedItem>;
}) => {
  // Upload files

  const attachmentReqs = Array.from(files)
    .map(converRFIFileToUploadDataTuple)
    .map(async ([rfiFile, ud]): Promise<RawRFIAttachment> => {
      const { signedurl, fileid } = await beginUpload(ud);

      await fetch(signedurl, {
        method: 'PUT',
        body: rfiFile.file,
      });

      const completeUpload = isAnswer
        ? completeRfiAnswerAttachmentUpload
        : completeRFIAttachmentUpload;

      const rawRFIAttachment = await completeUpload({
        projectid: rfiFile.projectid,
        parentid: rfiid,
        fileid,
        name: ud.filename,
        type: rfiFile.type,
      });

      return rawRFIAttachment;
    });

  const attachments = await (async () => {
    const addProjectDriveItemsToRFIRequest = addProjectDriveItemsToRFI({
      projectid,
      rfiid,
      projectDriveItems,
      isAnswer,
    });

    const { attachments: integrationAttachments } =
      await addIntegrationItemsToRFI({
        projectid,
        rfiid,
        integrationItems,
        isAnswer,
      });

    const updatedStudioFiles = studioItems
      ? studioItems.map(
          (f) =>
            new StudioImportRequest({
              id: f.id,
              studioprojectid: f.studioprojectid,
              fileName: f.name,
              filePath: f.path,
              fileSize: f.size,
            })
        )
      : List<StudioImportRequest>();

    const updatedFolders = studioData.studioSelectedFolders.map(
      (f) =>
        new StudioFolderImportRequest({
          id: f.id,
          studioprojectid: f.studioprojectid,
        })
    );

    let studioItemsToRFIRequest: RawRFIAttachment[] = [];
    if (updatedStudioFiles.size > 0 || updatedFolders.length > 0) {
      if (isAnswer) {
        studioItemsToRFIRequest = await addRfiAnswerAttachmentsFromStudio(
          projectid,
          rfiid,
          updatedStudioFiles.toArray(),
          updatedFolders
        );
      } else {
        studioItemsToRFIRequest = await addRfiAttachmentsFromStudio(
          projectid,
          rfiid,
          updatedStudioFiles.toArray(),
          updatedFolders
        );
      }
    }

    const localAttachments = await Promise.all(attachmentReqs);
    const projectDriveAttachments = (await addProjectDriveItemsToRFIRequest)
      .attachments;

    const importedAttachments = projectDriveAttachments.concat(
      studioItemsToRFIRequest
    );

    return localAttachments
      .concat(importedAttachments)
      .concat(integrationAttachments);
  })();
  return attachments;
};

export async function createRFI(
  rfi: NewRFI,
  files: Immutable.List<RFIFile>,
  projectDriveItems: List<ProjectDriveSelectedItem>,
  integrationItems: List<IntegrationsSelectedItem>,
  studioItems: List<StudioProjectFileData>,
  studioData: StudioData
): Promise<RFI> {
  const newRFIData = await new GQLRequest()
    .query(
      `
    mutation CreateRFI($input: NewRFI!) {
      createRfi(input: $input) {
        ${RFISchema}
      }
    }
    `
    )
    .variables({ input: rfi.serialize() })
    .json<{ createRfi: RawRFI }>();

  const { projectid, id: rfiid } = newRFIData.createRfi;

  const attachments = await attachFilesToRFI({
    projectid,
    rfiid,
    files,
    isAnswer: false,
    projectDriveItems,
    integrationItems,
    studioItems,
    studioData,
  });

  return RFI.deserialize({
    ...newRFIData.createRfi,
    attachments,
  });
}

export async function createRFIAnswer(
  rfi: RFI,
  answer: RFIAnswer,
  files: Immutable.List<RFIFile>,
  projectDriveItems: List<ProjectDriveSelectedItem>,
  integrationItems: List<IntegrationsSelectedItem>,
  studioItems: List<StudioProjectFileData>,
  studioData: StudioData
): Promise<RFIAnswer> {
  // Basically a RawRFIAnswer, but with "requestid" a.k.a rfiid.
  const input = {
    requestid: rfi.id,
    projectid: answer.projectid,
    answer: answer.answer,
    pageids: answer.pageids,
    attachmentcount: files.size,
    reviewedbyname: answer.reviewingname,
    reviewedbycompany: answer.reviewingcompany,
    reviewedbyemail: answer.reviewingemail,
    discussionattachmentids: rfi.discussionattachmentids,
  };

  const { createRfiAnswer: createdAnswer } = await new GQLRequest()
    .query(
      `
      mutation CreateRFIAnswer($input: NewRFIAnswer) {
        createRfiAnswer(input: $input) {
          id
          projectid
          answer
          pageids
          expectedAttachmentCount
          attachments {
            rfiid
            fileid
            name
            type
          }
          reportinguseremail
          respondingdate
          reviewingemail
          reviewingname
          reviewingcompany
        }
      }
      `
    )
    .variables({ input })
    .json<{ createRfiAnswer: RawRFIAnswer }>();

  const { projectid } = createdAnswer;

  const attachments = await attachFilesToRFI({
    projectid,
    rfiid: rfi.id,
    files,
    isAnswer: true,
    projectDriveItems,
    integrationItems,
    studioItems,
    studioData,
  });

  return RFIAnswer.deserialize({
    ...createdAnswer,
    attachments,
  });
}

export async function createRFIDraft(
  newRFIDraft: NewRFI,
  files: Immutable.List<RFIFile>,
  projectDriveItems: List<ProjectDriveSelectedItem>,
  integrationItems: List<IntegrationsSelectedItem>,
  studioItems: List<StudioProjectFileData>,
  studioData: StudioData
): Promise<RFI> {
  const newRFIDraftData = await new GQLRequest()
    .query(
      `
      mutation CreateRfiDraft($input: NewRFI!) {
        createRfiDraft(input: $input) {
          ${RFISchema}
        }
      }
      `
    )
    .variables({ input: newRFIDraft })
    .json<{ createRfiDraft: RawRFI }>();

  const { projectid, id: rfiid } = newRFIDraftData.createRfiDraft;

  const attachments = await attachFilesToRFI({
    projectid,
    rfiid,
    files,
    isAnswer: false,
    projectDriveItems,
    integrationItems,
    studioItems,
    studioData,
  });

  return RFI.deserialize({
    ...newRFIDraftData.createRfiDraft,
    attachments,
  });
}

export async function updateRFIDraft(
  rfiid: RFIId,
  newRFI: NewRFI,
  files: Immutable.List<RFIFile>,
  attachmentsToDelete: Immutable.List<RFIAttachment>,
  projectDriveItems: List<ProjectDriveSelectedItem>,
  integrationItems: List<IntegrationsSelectedItem>,
  studioItems: List<StudioProjectFileData>,
  studioData: StudioData
): Promise<RFI> {
  const draftUpdate: RFIDraftUpdate = {
    rfiid,
    newdata: newRFI.serialize(),
    attachmentsToDelete: attachmentsToDelete.map((a) => a.fileid).toArray(),
  };

  const { updateRfiDraft: updatedRFIDraft } = await new GQLRequest()
    .query(
      `
      mutation UpdateRfiDraft($draftUpdate: RFIDraftUpdate) {
        updateRfiDraft(draft: $draftUpdate) {
          ${RFISchema}
        }
      }
      `
    )
    .variables({ draftUpdate })
    .json<{ updateRfiDraft: RawRFI }>();

  const { projectid } = updatedRFIDraft;

  const attachments = await attachFilesToRFI({
    projectid,
    rfiid,
    files,
    isAnswer: false,
    projectDriveItems,
    integrationItems,
    studioItems,
    studioData,
  });

  return RFI.deserialize({
    ...updatedRFIDraft,
    attachments: updatedRFIDraft.attachments.concat(attachments),
  });
}

export async function deleteRFIDraft(
  rfiid: string,
  projectid: string,
  previousversionid: string | null = null
): Promise<void> {
  await new GQLRequest()
    .query(
      `
      mutation DeleteRfiDraft($projectid: String!, $rfiid: String!, $previousversionid: String) {
        deleteRfiDraft(projectid: $projectid, rfiid: $rfiid, previousversionid: $previousversionid)
      }
      `
    )
    .variables({ rfiid, projectid, previousversionid })
    .run();
}

export async function publishRFIDraft(
  rfiid: RFIId,
  newRFI: NewRFI,
  files: Immutable.List<RFIFile>,
  attachmentsToDelete: Immutable.List<RFIAttachment>,
  projectDriveItems: List<ProjectDriveSelectedItem>,
  integrationItems: List<IntegrationsSelectedItem>,
  studioItems: List<StudioProjectFileData>,
  studioData: StudioData
): Promise<RFI> {
  const draftUpdate: RFIDraftUpdate = {
    rfiid,
    newdata: newRFI.serialize(),
    attachmentsToDelete: attachmentsToDelete.map((a) => a.fileid).toArray(),
  };

  const { publishRfiDraft: publishedRfi } = await new GQLRequest()
    .query(
      `
      mutation PublishRfiDraft($draft: RFIDraftUpdate) {
        publishRfiDraft(draft: $draft) {
          ${RFISchema}
        }
      }
      `
    )
    .variables({ draft: draftUpdate })
    .json<{ publishRfiDraft: RawRFI }>();

  const { projectid } = publishedRfi;

  const attachments = await attachFilesToRFI({
    projectid,
    rfiid,
    files,
    isAnswer: false,
    projectDriveItems,
    integrationItems,
    studioItems,
    studioData,
  });

  return RFI.deserialize({
    ...publishedRfi,
    attachments: publishedRfi.attachments.concat(attachments),
  });
}

export async function getRFIReport(
  projectid: string,
  rfiid: string
): Promise<string> {
  const { generateRfi } = await new GQLRequest()
    .query(
      `
    query rfi($projectid: String!, $rfiid: String!) {
      generateRfi(projectid: $projectid, rfiid: $rfiid) {
        url
      }
    }
    `
    )
    .variables({ projectid, rfiid })
    .json<{ generateRfi: { url: string } }>();

  return generateRfi.url;
}

// generateRfiLog(projectid: String!): ExportItem!
export async function exportXLSXRFis(
  projectid: string,
  rfiids: Immutable.List<RFIId>,
  sortfield: string,
  sortdirection: ExportSortDirection
): Promise<RFIExportItem> {
  const { exportRFILog } = await new GQLRequest()
    .query(
      `
      query ExportRFILog($projectid: String!, $rfiids: [String!], $sortfield: String, $sortdirection: ExportSortDirection) {
        exportRFILog(projectid: $projectid, rfiids: $rfiids, sortfield: $sortfield, sortdirection: $sortdirection) {
          url
          extension
        }
      }
      `
    )
    .variables({
      projectid,
      rfiids: rfiids.toArray(),
      sortfield,
      sortdirection,
    })
    .json<{ exportRFILog: RFIExportItem }>();

  return exportRFILog;
}

interface BatchRFIMarkupPost {
  markupid: string;
  fileid: string;
  jsonBlob: string;
  resources: string[];
}

export async function postMarkupsToRFI(
  rfi: RFI,
  markups: Immutable.List<Markup>
): Promise<RFI> {
  const batchRFIMarkupPosts = markups
    .map((m) => {
      const post: BatchRFIMarkupPost = {
        markupid: m.markupid,
        fileid: m.fileid,
        jsonBlob: m.jsonBlob,
        resources: [],
      };

      return post;
    })
    .toArray();

  const stuff = await new GQLRequest()
    .query(
      `
      mutation PostMarkupsToRFI($rfiid: String!, $projectid: String!, $markups: [BatchRFIMarkupPost!]) {
        postMarkupsToRFI(rfiid: $rfiid, projectid: $projectid, markups: $markups) {
          ${RFISchema}
        }
      }
      `
    )
    .variables({
      rfiid: rfi.id,
      projectid: rfi.projectid,
      markups: batchRFIMarkupPosts,
    })
    .json<{ postMarkupsToRFI: RawRFI }>();

  return RFI.deserialize(stuff.postMarkupsToRFI);
}

export async function modifyRFIForwardingList(
  rfiid: RFIId,
  projectid: string,
  forwardList: OrderedSet<string>
): Promise<RFI> {
  const emailaddresses = forwardList.map((v) => v).toArray();

  const { modifyRfiForwardingList } = await new GQLRequest()
    .query(
      `
      mutation ModifyRfiForwardingList($rfiid: String!, $projectid: String!, $emailaddresses: [String!]!) {
        modifyRfiForwardingList(rfiid: $rfiid, projectid: $projectid, emailaddresses: $emailaddresses) {
          ${RFISchema}
        }
      }
      `
    )
    .variables({ rfiid, projectid, emailaddresses })
    .json<{ modifyRfiForwardingList: RawRFI }>();

  return RFI.deserialize({ ...modifyRfiForwardingList });
}

export async function createRFIForwardActivity(
  rfi: RFI,
  projectid: string,
  files: Immutable.List<RFIFile>,
  projectDriveItems: List<ProjectDriveSelectedItem>,
  integrationItems: List<IntegrationsSelectedItem>,
  studioItems: List<StudioProjectFileData>,
  studioData: StudioData,
  forwardActivity: NewRFIForwardActivity
): Promise<RFI> {
  const attachments = await attachFilesToRFI({
    projectid,
    rfiid: rfi.id,
    files,
    isAnswer: false,
    projectDriveItems,
    integrationItems,
    studioItems,
    studioData,
  });

  const uploads = attachments.map(
    (a) =>
      new NewRFIAttachment({
        projectid,
        parentid: rfi.id,
        fileid: a.fileid,
        name: a.name,
        type: a.type,
      })
  );

  const input = {
    rfiid: rfi.id,
    projectid,
    message: forwardActivity.message,
    attachments: uploads,
    pageids: forwardActivity.pageids.valueSeq().toArray(),
  };

  const { createRfiForwardActivity } = await new GQLRequest()
    .query(
      `
      mutation CreateRfiForwardActivity($input: NewRFIForwardActivity) {
        createRfiForwardActivity(input: $input) {
          ${RFISchema}
        }
      }
      `
    )
    .variables({ input })
    .json<{ createRfiForwardActivity: RawRFI }>();

  return RFI.deserialize({
    ...createRfiForwardActivity,
  });
}

export async function attachRfiForwardDiscussions(
  rfi: RFI,
  projectid: string,
  discussionIds: Immutable.Set<string>
): Promise<RFI> {
  const ids = discussionIds.map((v) => v).toArray();
  const rfiid = rfi.id;

  const { attachRfiForwardDiscussions: attachDiscussions } =
    await new GQLRequest()
      .query(
        `
      mutation AttachRfiForwardDiscussions($rfiid: String!, $projectid: String!, $ids: [String!]!) {
        attachRfiForwardDiscussions(rfiid: $rfiid, projectid: $projectid, ids: $ids) {
          ${RFISchema}
        }
      }
      `
      )
      .variables({ rfiid, projectid, ids })
      .json<{ attachRfiForwardDiscussions: RawRFI }>();

  return RFI.deserialize({ ...attachDiscussions });
}

export async function deleteRfiForwardDiscussion(
  rfiid: string,
  projectid: string,
  id: string
): Promise<RFI> {
  const { deleteRfiForwardDiscussion: deleteDiscussion } =
    await new GQLRequest()
      .query(
        `
      mutation DeleteRfiForwardDiscussion($rfiid: String!, $projectid: String!, $id: String!) {
        deleteRfiForwardDiscussion(rfiid: $rfiid, projectid: $projectid, id: $id) {
          ${RFISchema}
        }
      }
      `
      )
      .variables({ rfiid, projectid, id })
      .json<{ deleteRfiForwardDiscussion: RawRFI }>();

  return RFI.deserialize({ ...deleteDiscussion });
}

export async function reopenRFI(
  rfi: NewRFI,
  files: Immutable.List<RFIFile>,
  projectDriveItems: List<ProjectDriveSelectedItem>,
  integrationItems: List<IntegrationsSelectedItem>,
  studioItems: List<StudioProjectFileData>,
  studioData: StudioData
): Promise<RFI> {
  const newRFIData = await new GQLRequest()
    .query(
      `
    mutation ReopenRFI($input: NewRFI!) {
      reopenRfi(input: $input) {
        ${RFISchema}
      }
    }
    `
    )
    .variables({ input: rfi.serialize() })
    .json<{ reopenRfi: RawRFI }>();

  const { projectid } = rfi;
  const {
    reopenRfi: { id: rfiid },
  } = newRFIData;

  const attachments = await attachFilesToRFI({
    projectid,
    rfiid,
    files,
    isAnswer: false,
    projectDriveItems,
    integrationItems,
    studioItems,
    studioData,
  });

  return RFI.deserialize({
    ...newRFIData.reopenRfi,
    attachments,
  });
}
